import { useState, useContext } from 'react';
import { LocalizationContext } from 'src/Context/LocalizationContext';
import { errorHandler } from 'src/Config/ErrorHandler';
import { DataContext } from 'src/Context/DataContext';
import { host } from 'src/Branding/config';

export const useQuerySendQuestionaryByEmail = (): {
	sendQuestionaryByEmail: (recipient: string) => any;
	isLoading: boolean;
	errorMessage: string | null;
	resetErrorMessage: () => void;
} => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const { sc: shortCode } = useContext(DataContext);
	const { FileUpload, LanguageCode } = useContext(LocalizationContext);
	const { UploadFromComputerEmailSentErrorMessage } = FileUpload;

	const resetErrorMessage = () => {
		setErrorMessage(null);
	};

	const sendQuestionaryByEmail = async (recipient: string) => {
		if (!shortCode) {
			setErrorMessage('Please enter a valid short code');
			errorHandler(new Error('Failed to send questionnaire'), {
				context: 'missing_shortcode',
				recipient,
			});
			return;
		}

		if (!recipient) {
			setErrorMessage('Please enter a valid email address');
			errorHandler(new Error('Failed to send questionnaire'), {
				context: 'missing_email',
				shortCode,
			});
			return;
		}

		// Validate email
		const isEmailValid = recipient
			.trim()
			.match(
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
			);

		if (!isEmailValid) {
			setErrorMessage('Email address is invalid');
			errorHandler(new Error('Failed to send questionnaire'), {
				context: 'invalid_email',
				recipient,
				shortCode,
			});
		}

		setIsLoading(true);

		const url = new URL(host + '/api/sendQuestionaryByEmail');
		const requestOptions = {
			method: 'POST',
			withCredentials: true,
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ sc: shortCode, email: recipient, localeTag: LanguageCode }),
		};

		return await fetch(url, requestOptions)
			.then((res) => {
				return res.json();
			})
			.then((resData) => {
				if (resData.status === 'error') {
					throw new Error(resData.message);
				}
				return resData;
			})
			.catch((error) => {
				errorHandler(new Error('Failed to send questionnaire'), {
					context: 'api_error',
					originalError: error,
				});
				setErrorMessage(UploadFromComputerEmailSentErrorMessage);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return { sendQuestionaryByEmail, isLoading, errorMessage, resetErrorMessage };
};
